import { useState, useEffect } from 'react'
import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { AlertCircle, FileText } from 'lucide-react'
import { useTranslation } from '@/i18n'

export interface DuplicateFile {
  fileName: string
  existingDocumentId: string
}

export interface BulkDuplicateModalProps {
  isOpen: boolean
  onClose: () => void
  duplicateFiles: DuplicateFile[]
  newFilesCount: number
  onReplace: () => void
  onKeepBoth: () => void
  onSkip: () => void
}

export type BulkDuplicateAction = 'replace' | 'keep-both' | 'skip'

const BulkDuplicateModal = ({
  isOpen,
  onClose,
  duplicateFiles,
  newFilesCount,
  onReplace,
  onKeepBoth,
  onSkip,
}: BulkDuplicateModalProps) => {
  const { t } = useTranslation()
  const [selectedAction, setSelectedAction] = useState<BulkDuplicateAction | null>(null)

  useEffect(() => {
    if (isOpen) {
      setSelectedAction(null)
    }
  }, [isOpen])

  const handleUpload = () => {
    if (selectedAction === 'replace') {
      onReplace()
    } else if (selectedAction === 'keep-both') {
      onKeepBoth()
    } else if (selectedAction === 'skip') {
      onSkip()
    }
  }

  const duplicateCount = duplicateFiles.length

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('data_catalog:bulk_duplicate_modal.title', { count: duplicateCount })}
      overlayClassName="bg-black/10"
    >
      <div className="space-y-4">
        {/* Warning message */}
        <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('data_catalog:bulk_duplicate_modal.message', { count: duplicateCount })}
            </p>
          </div>
        </div>

        {/* List of duplicate files */}
        <div className="max-h-40 overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-dark-muted">
          <div className="space-y-2">
            {duplicateFiles.map((file, index) => (
              <div key={index} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <FileText className="h-4 w-4 text-gray-500 dark:text-dark-text-muted flex-shrink-0 mt-0.5" />
                <span className="break-all">{file.fileName}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Radio button options */}
        <div className="space-y-3">
          {/* Option 1: Replace all */}
          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors min-w-0 overflow-hidden ${
              selectedAction === 'replace'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <input
              type="radio"
              name="bulk-duplicate-action"
              value="replace"
              checked={selectedAction === 'replace'}
              onChange={() => setSelectedAction('replace')}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-gray-900 dark:text-dark-text">
                {t('data_catalog:bulk_duplicate_modal.option_replace')}
              </div>
              <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                {t('data_catalog:bulk_duplicate_modal.option_replace_description', { count: duplicateCount })}
              </div>
            </div>
          </label>

          {/* Option 2: Keep both */}
          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors min-w-0 overflow-hidden ${
              selectedAction === 'keep-both'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <input
              type="radio"
              name="bulk-duplicate-action"
              value="keep-both"
              checked={selectedAction === 'keep-both'}
              onChange={() => setSelectedAction('keep-both')}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-gray-900 dark:text-dark-text">
                {t('data_catalog:bulk_duplicate_modal.option_keep_both')}
              </div>
              <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                {t('data_catalog:bulk_duplicate_modal.option_keep_both_description', { count: duplicateCount })}
              </div>
            </div>
          </label>

          {/* Option 3: Skip duplicates */}
          <label
            className={`flex items-start gap-3 p-3 border rounded-md transition-colors min-w-0 overflow-hidden ${
              newFilesCount === 0
                ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-dark-border'
                : selectedAction === 'skip'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer'
            }`}
          >
            <input
              type="radio"
              name="bulk-duplicate-action"
              value="skip"
              checked={selectedAction === 'skip'}
              onChange={() => setSelectedAction('skip')}
              disabled={newFilesCount === 0}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 disabled:cursor-not-allowed flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-gray-900 dark:text-dark-text">
                {t('data_catalog:bulk_duplicate_modal.option_skip')}
              </div>
              <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                {t('data_catalog:bulk_duplicate_modal.option_skip_description', { count: newFilesCount })}
              </div>
            </div>
          </label>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('common:buttons.cancel')}
          </Button>
          <Button onClick={handleUpload} disabled={!selectedAction}>
            {t('common:buttons.upload')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default BulkDuplicateModal
